import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Armchair, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { branchInput } from '@barbeiropro/contracts';
import { AddressFields } from '@/components/shared/address-fields';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { MaskedInput } from '@/components/shared/masked-input';
import { EmptyState, ErrorState, LoadingState } from '@/components/shared/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { api, errorMessage } from '@/lib/api';
import { maskCep, maskPhone } from '@/lib/format';
import type { Branch } from '@/lib/org';
import { useChairs, type Chair } from '../catalog/api';
import { applyServerError, emptyToNull, zodForm } from '../catalog/lib';
import { Section } from './section';

interface Values {
  name: string;
  phone: string;
  active: boolean;
  address: { cep: string; state: string; city: string; district: string; street: string; number: string; complement: string };
}
const emptyValues: Values = { name: '', phone: '', active: true, address: { cep: '', state: '', city: '', district: '', street: '', number: '', complement: '' } };
const toPayload = (v: Values) => ({ ...v, phone: emptyToNull(v.phone) });

export function BranchesPage() {
  const branches = useQuery({ queryKey: ['org', 'branches'], queryFn: () => api.get<Branch[]>('/org/branches') });
  const chairs = useChairs();
  const [editing, setEditing] = useState<Branch | null>(null);
  const [open, setOpen] = useState(false);

  const openForm = (b: Branch | null) => {
    setEditing(b);
    setOpen(true);
  };

  return (
    <Section
      title="Unidades e cadeiras"
      description="Cada unidade tem endereço, horário e cadeiras próprias. Os profissionais são vinculados a uma unidade."
      actions={
        <Button onClick={() => openForm(null)}>
          <Plus aria-hidden /> Nova unidade
        </Button>
      }
    >
      {branches.isLoading ? (
        <LoadingState />
      ) : branches.error ? (
        <ErrorState error={branches.error} onRetry={() => branches.refetch()} />
      ) : branches.data?.length === 0 ? (
        <EmptyState title="Nenhuma unidade cadastrada" description="Cadastre o endereço da barbearia para liberar horários e agenda." action={<Button onClick={() => openForm(null)}><Plus aria-hidden /> Nova unidade</Button>} />
      ) : (
        <ul className="grid gap-4">
          {branches.data?.map((b) => (
            <li key={b.id} className="rounded-lg border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold">{b.name}</h3>
                    <Badge variant={b.active ? 'secondary' : 'outline'}>{b.active ? 'Ativa' : 'Inativa'}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {b.address.street}, {b.address.number}
                    {b.address.complement ? ` - ${b.address.complement}` : ''} · {b.address.district} · {b.address.city}/{b.address.state} · CEP {maskCep(b.address.cep)}
                  </p>
                  {b.phone && <p className="tabular text-sm text-muted-foreground">{maskPhone(b.phone)}</p>}
                </div>
                <Button variant="outline" size="sm" onClick={() => openForm(b)}>
                  <Pencil aria-hidden /> Editar unidade
                </Button>
              </div>
              <ChairList branchId={b.id} chairs={chairs.data?.filter((c) => c.branchId === b.id) ?? []} loading={chairs.isLoading} />
            </li>
          ))}
        </ul>
      )}
      <BranchModal open={open} onOpenChange={setOpen} branch={editing} />
    </Section>
  );
}

function ChairList({ branchId, chairs, loading }: { branchId: string; chairs: Chair[]; loading: boolean }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['org', 'chairs'] });
    qc.invalidateQueries({ queryKey: ['barbers'] });
  };
  const add = useMutation({
    mutationFn: () => api.post('/org/chairs', { branchId, name: name.trim(), active: true }),
    onSuccess: () => {
      toast.success('Cadeira salva');
      setName('');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="mt-4 border-t pt-4">
      <h4 className="flex items-center gap-2 text-sm font-medium">
        <Armchair className="size-4" aria-hidden /> Cadeiras
      </h4>
      {loading ? (
        <p className="mt-2 text-sm text-muted-foreground">Carregando…</p>
      ) : chairs.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">Nenhuma cadeira. Cadastre para vincular profissionais a um lugar fixo.</p>
      ) : (
        <ul className="mt-2 grid gap-2">
          {chairs.map((c) => (
            <ChairRow key={c.id} chair={c} onChanged={refresh} />
          ))}
        </ul>
      )}
      <form
        className="mt-3 flex max-w-md gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) add.mutate();
        }}
      >
        <label htmlFor={`chair-new-${branchId}`} className="sr-only">
          Nome da nova cadeira
        </label>
        <Input id={`chair-new-${branchId}`} value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Cadeira 1" maxLength={60} />
        <Button type="submit" variant="outline" disabled={!name.trim() || add.isPending}>
          <Plus aria-hidden /> Adicionar cadeira
        </Button>
      </form>
    </div>
  );
}

function ChairRow({ chair, onChanged }: { chair: Chair; onChanged: () => void }) {
  const [name, setName] = useState(chair.name);
  useEffect(() => setName(chair.name), [chair.name]);
  const update = useMutation({
    mutationFn: (p: { name: string; active: boolean }) => api.put(`/org/chairs/${chair.id}`, p),
    onSuccess: () => {
      toast.success('Cadeira salva');
      onChanged();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const dirty = name.trim() !== chair.name && name.trim().length > 0;
  return (
    <li className="flex flex-wrap items-center gap-2">
      <Input value={name} onChange={(e) => setName(e.target.value)} aria-label={`Nome da cadeira ${chair.name}`} className="max-w-56" maxLength={60} />
      {dirty && (
        <Button size="sm" variant="outline" disabled={update.isPending} onClick={() => update.mutate({ name: name.trim(), active: chair.active })}>
          Salvar cadeira
        </Button>
      )}
      <label className="flex items-center gap-2 text-sm">
        <Switch checked={chair.active} onCheckedChange={(v) => update.mutate({ name: chair.name, active: v })} aria-label={`Cadeira ${chair.name} ativa`} />
        {chair.active ? 'Ativa' : 'Inativa'}
      </label>
      <ConfirmDialog
        trigger={
          <Button size="icon" variant="ghost" aria-label={`Excluir cadeira ${chair.name}`}>
            <Trash2 className="text-destructive" aria-hidden />
          </Button>
        }
        title={`Excluir a cadeira "${chair.name}"?`}
        description="Os profissionais que usam esta cadeira ficam sem cadeira fixa. Os agendamentos existentes são mantidos."
        confirmLabel="Excluir cadeira"
        onConfirm={async () => {
          try {
            await api.del(`/org/chairs/${chair.id}`);
            toast.success('Cadeira excluída');
            onChanged();
          } catch (e) {
            toast.error(errorMessage(e));
          }
        }}
      />
    </li>
  );
}

function BranchModal({ open, onOpenChange, branch }: { open: boolean; onOpenChange: (v: boolean) => void; branch: Branch | null }) {
  const qc = useQueryClient();
  const form = useForm<Values>({ resolver: zodForm<Values>(branchInput, toPayload), defaultValues: emptyValues });
  const { errors } = form.formState;

  useEffect(() => {
    if (!open) return;
    form.reset(
      branch
        ? {
            name: branch.name,
            phone: branch.phone ? maskPhone(branch.phone) : '',
            active: branch.active,
            address: { ...branch.address, cep: maskCep(branch.address.cep), complement: branch.address.complement ?? '' },
          }
        : emptyValues,
    );
  }, [open, branch]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v: Values) => {
      const body = branchInput.parse(toPayload(v));
      return branch ? api.put(`/org/branches/${branch.id}`, body) : api.post('/org/branches', body);
    },
    onSuccess: () => {
      toast.success('Unidade salva');
      qc.invalidateQueries({ queryKey: ['org'] });
      qc.invalidateQueries({ queryKey: ['barbers'] });
      onOpenChange(false);
    },
    onError: (e) => applyServerError(form, e),
  });

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={branch ? 'Editar unidade' : 'Nova unidade'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button type="submit" form="branch-form" disabled={save.isPending}>
            {save.isPending ? 'Salvando…' : 'Salvar unidade'}
          </Button>
        </>
      }
    >
      <form id="branch-form" onSubmit={form.handleSubmit((v) => save.mutate(v))} className="grid gap-5" noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="br-name" label="Nome da unidade" error={errors.name?.message}>
            <Input id="br-name" autoFocus aria-invalid={!!errors.name} {...form.register('name')} />
          </Field>
          <Field id="br-phone" label="Telefone (opcional)" error={errors.phone?.message}>
            <MaskedInput mask="phone" id="br-phone" aria-invalid={!!errors.phone} {...form.register('phone')} />
          </Field>
        </div>
        <AddressFields form={form} prefix="address" />
        <Controller
          control={form.control}
          name="active"
          render={({ field }) => (
            <label className="flex items-center justify-between gap-3 rounded-md border p-3 text-sm">
              <span>
                <span className="font-medium">Unidade ativa</span>
                <span className="block text-xs text-muted-foreground">Inativa deixa de receber agendamentos.</span>
              </span>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </label>
          )}
        />
      </form>
    </FormModal>
  );
}
